/**
 * 7 core pattern — docs/DESIGN_TOKENS.md, docs/SPEC.md.
 *
 * GlassCard · GlassCardPremium · Chip · CTAButton · Toggle · Toast · Avatar
 *
 * Uslublar `ui.css` da, tokenlar orqali. Bu yerda faqat tuzilish va xatti-harakat.
 */

import { Icon, paletteVars, type IconName } from '@voice/ui-tokens';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { haptic } from '../app/telegram';
import './ui.css';

/* ── 1. GlassCard ── */

export function GlassCard({
  children,
  variant = 'default',
  className = '',
  ...rest
}: {
  children: ReactNode;
  variant?: 'default' | 'soft' | 'subtle' | 'strong';
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const mod = variant === 'default' ? '' : ` card--${variant}`;
  return (
    <div className={`card${mod} ${className}`} {...rest}>
      {children}
    </div>
  );
}

/* ── 2. GlassCardPremium — 1px gradient chegara ── */

export function GlassCardPremium({
  children,
  glow = true,
  className = '',
}: {
  children: ReactNode;
  glow?: boolean;
  className?: string;
}) {
  return (
    <div className={`premium ${className}`}>
      <div className="premium__inner">
        {glow && <div className="premium__glow" />}
        <div className="premium__content">{children}</div>
      </div>
    </div>
  );
}

/* ── 3. Chip ── */

export function Chip({
  active = false,
  children,
  onClick,
  className = '',
  ...rest
}: {
  active?: boolean;
  children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'>) {
  return (
    <button
      type="button"
      // `className` ATAYLAB alohida olinadi. Aks holda u `...rest` ichida
      // qolib, quyidagi tarqatishda `chip` sinfini ustidan yozib yuboradi
      // va chip butunlay uslubsiz tugmaga aylanadi. Xato jim: TypeScript
      // ham, lint ham hech narsa demaydi.
      className={`chip ${className}`.trim()}
      aria-pressed={active}
      onClick={(e) => {
        haptic('light');
        onClick?.(e);
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ── 4. CTAButton — har birida --specular ── */

export type CtaVariant = 'primary' | 'gift' | 'match' | 'buy' | 'danger' | 'ghost';

export function CTAButton({
  children,
  variant = 'primary',
  size,
  price,
  onClick,
  className = '',
  ...rest
}: {
  children: ReactNode;
  variant?: CtaVariant;
  size?: 'sm';
  /** CTA ichidagi narx yorlig'i. Qiymat CONFIG'dan keladi, qattiq yozilmaydi. */
  price?: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'>) {
  const mod = variant === 'primary' ? '' : ` cta--${variant}`;
  const sizeMod = size ? ` cta--${size}` : '';
  return (
    <button
      type="button"
      // `className` alohida olinadi — `Chip` dagi bilan bir xil sabab.
      className={`cta${mod}${sizeMod} ${className}`.trim()}
      onClick={(e) => {
        haptic('light');
        onClick?.(e);
      }}
      {...rest}
    >
      <span>{children}</span>
      {price !== undefined && <span className="cta__price tnum">{price}</span>}
    </button>
  );
}

/* ── 5. Toggle ── */

export function Toggle({
  checked,
  onChange,
  size,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  size?: 'sm';
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`toggle${size ? ' toggle--sm' : ''}`}
      onClick={() => {
        haptic('light');
        onChange(!checked);
      }}
    >
      <span className="toggle__knob" />
    </button>
  );
}

/* ── 6. Toast ── */

export function Toast({ message }: { message: string }) {
  return (
    <div className="toast-layer">
      <div className="toast" role="status">
        {message}
      </div>
    </div>
  );
}

/* ── 7. Avatar ── */

export function Avatar({
  letter,
  paletteIdx,
  size = 44,
  speaking = false,
  muted = false,
}: {
  letter: string;
  /** SERVERDAN keladi (`users.palette_idx`), bu yerda hisoblanmaydi (D-042). */
  paletteIdx: number;
  size?: number;
  speaking?: boolean;
  muted?: boolean;
}) {
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
      {speaking && <div className="avatar__ring" />}
      <div
        className={`avatar${speaking ? ' avatar--speaking' : ''}`}
        style={{
          ...paletteVars(paletteIdx),
          width: size,
          height: size,
          fontSize: Math.round(size * 0.34),
        }}
      >
        {letter}
      </div>
      {muted && (
        <div className="avatar__badge">
          <Icon name="micoff" size={9} color="var(--danger-soft)" />
        </div>
      )}
    </div>
  );
}

export { Icon, type IconName };
